import { getNumberString } from './getNumberString';
import i18next from 'i18next';
import SSPContentFieldAccessor from 'sspcontentext/src/model/accessor/SSPContentFieldAccessor';
import { getSetup } from './getSetup';

export type ConsumptionElementType = {
	key: string;
	headline: string;
	key2?: string;
	unit?: string;
	unit2?: string;
	digits?: number;
	round?: true;
	engine?: boolean;
	string?: boolean;
	disclaimer?: string;
	efficiency?: true;
	hideIfEmpty?: true;
	overwrite?: number;
	subtitle?: boolean;
	bold?: boolean;
};

export type ConsumptionListType = {
	Electric: Array<ConsumptionElementType>;
	HybridPetrol: Array<ConsumptionElementType>;
	Diesel: Array<ConsumptionElementType>;
	Petrol: Array<ConsumptionElementType>;
	Gas: Array<ConsumptionElementType>;
};

const setup = getSetup();

const generateMexicoConsumptionList = (
	unit: string,
	overwrite?: number
): ConsumptionElementType[] => [
	{
		key: 'CityEmissions',
		headline: 'MexicoCityEmissions',
		unit,
		digits: 1,
		...(overwrite !== undefined ? { overwrite } : {}),
	},
	{
		key: 'HighwayEmissions',
		headline: 'MexicoHighwayEmissions',
		unit,
		digits: 1,
		...(overwrite !== undefined ? { overwrite } : {}),
	},
	{
		key: 'ObservedCombinedCO2Emissions',
		headline: 'MexicoObservedCombinedCO2Emissions',
		unit,
		digits: 1,
		...(overwrite !== undefined ? { overwrite } : {}),
	},
	{
		key: 'CityFuelEfficiency',
		headline: 'MexicoCityFuelEfficiency',
		unit: 'WltpMexicoEfficiencyUnit',
		digits: 1,
		...(overwrite !== undefined ? { overwrite } : {}),
	},
	{
		key: 'HighwayFuelEfficiency',
		headline: 'MexicoHighwayFuelEfficiency',
		unit: 'WltpMexicoEfficiencyUnit',
		digits: 1,
		...(overwrite !== undefined ? { overwrite } : {}),
	},
	{
		key: 'ObservedCombinedEfficiency',
		headline: 'MexicoObservedCombinedEfficiency',
		unit: 'WltpMexicoEfficiencyUnit',
		digits: 1,
		...(overwrite !== undefined ? { overwrite } : {}),
	},
];

const consumptionListMexico: { wltp: ConsumptionListType } = {
	wltp: {
		Electric: generateMexicoConsumptionList('WltpMexicoEmissionUnit', 0),
		HybridPetrol: generateMexicoConsumptionList('WltpMexicoEmissionUnit'),
		Diesel: generateMexicoConsumptionList('WltpMexicoEmissionUnit'),
		Petrol: generateMexicoConsumptionList('WltpMexicoEmissionUnit'),
		Gas: generateMexicoConsumptionList('WltpMexicoEmissionUnit'),
	},
};

const generateConsumptionList = (unit: string): ConsumptionElementType[] => [
	{
		key: 'wltp_consumption_sustaining_combined',
		headline: 'WltpFuelConsumptionCombined',
		unit,
		digits: 1,
		bold: true,
	},
	{
		key: 'wltp_consumption_low',
		headline: 'WltpFuelConsumptionLow',
		unit,
		digits: 1,
	},
	{
		key: 'wltp_consumption_medium',
		headline: 'WltpFuelConsumptionMedium',
		unit,
		digits: 1,
	},
	{
		key: 'wltp_consumption_high',
		headline: 'WltpFuelConsumptionHigh',
		unit,
		digits: 1,
	},
	{
		key: 'wltp_consumption_extra_high',
		headline: 'WltpFuelConsumptionExtraHigh',
		unit,
		digits: 1,
	},
	{
		key: 'wltp_co2_sustaining_combined',
		headline: 'WltpCO2Combined',
		unit: 'CO2ConsumptionUnit',
		disclaimer: 'consumption.carbon-dioxide-emissions',
	},
];

export const consumptionList: { wltp?: ConsumptionListType } =
	setup?.market?.name === 'MEX'
		? consumptionListMexico
		: {
				wltp: {
					Electric: [
						{
							key: 'wltp_consumption_electrical_combined',
							headline: 'WltpElectricConsumptionCombined',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
							bold: true,
						},
						{
							key: 'wltp_consumption_electrical_low',
							headline: 'WltpFuelConsumptionLow',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_consumption_electrical_medium',
							headline: 'WltpFuelConsumptionMedium',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_consumption_electrical_high',
							headline: 'WltpFuelConsumptionHigh',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_consumption_electrical_extra_high',
							headline: 'WltpFuelConsumptionExtraHigh',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_co2_electrical_combined',
							headline: 'WltpCO2Combined',
							unit: 'CO2ConsumptionUnit',
							disclaimer: 'consumption.carbon-dioxide-emissions',
						},
						{
							key: 'wltp_range_combined',
							headline: 'WltpElectricalRangeCombined',
							unit: 'LengthUnit',
							round: true,
						},
						{
							key: 'wltp_range_city',
							headline: 'WltpElectricalRangeUrban',
							unit: 'LengthUnit',
							round: true,
						},
						{
							key: 'battery_data',
							headline: 'BatteryData',
							subtitle: true,
						},
						{
							key: 'battery_size',
							headline: 'OverviewBoxBattery',
							unit: 'BatteryUnit',
							engine: true,
						},
						{
							key: 'battery_charging_dc_100',
							headline: 'BatteryChargingDc100',
							engine: true,
							string: true,
						},
						{
							key: 'battery_charging_dc_max_power',
							headline: 'OverviewBoxChargingMaxPower',
							unit: 'FuelPowerUnit',
							round: true,
							engine: true,
						},
						{
							key: 'battery_charging_ac_100',
							headline: 'BatteryChargingAc100',
							engine: true,
							string: true,
						},
						{
							key: 'battery_charging_bidirectional',
							headline: 'OverviewBoxChargingBidirectional',
							engine: true,
							string: true,
						},
						{
							key: 'battery_charging_range_dc',
							headline: 'OverviewBoxChargingDCRange',
							unit: 'LengthUnit',
							round: true,
							engine: true,
							string: true,
						},
						{
							key: 'battery_nominal_voltage',
							headline: 'OverviewBoxBatteryNominalVoltage',
							unit: 'BatteryNominalVoltageUnit',
							round: true,
							engine: true,
							string: true,
						},
						{
							key: 'battery_type',
							headline: 'OverviewBoxBatteryType',
							engine: true,
							string: true,
						},
					],
					HybridPetrol: [
						{
							key: 'fuel_consumption_discharged_battery',
							headline: 'FuelConsumptionDischargedBattery',
							subtitle: true,
						},
						...generateConsumptionList('FuelConsumptionUnit'),
						{
							key: 'fuel_consumption_fully_electrical_use',
							headline: 'FuelConsumptionFullyElectricalUse',
							subtitle: true,
						},
						{
							key: 'wltp_consumption_electrical_combined',
							headline: 'WltpElectricConsumptionCombined',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
							bold: true,
						},
						{
							key: 'wltp_consumption_electrical_low',
							headline: 'WltpFuelConsumptionLow',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_consumption_electrical_medium',
							headline: 'WltpFuelConsumptionMedium',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_consumption_electrical_high',
							headline: 'WltpFuelConsumptionHigh',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_consumption_electrical_extra_high',
							headline: 'WltpFuelConsumptionExtraHigh',
							unit: 'ElectricConsumptionUnit',
							digits: 1,
						},
						{
							key: 'values_weighted',
							headline: 'ValuesWeighted',
							subtitle: true,
						},
						{
							key: 'wltp_consumption_electrical_weighted_combined',
							key2: 'wltp_consumption_weighted_combined',
							headline: 'WltpConsumptionWeightedCombined',
							unit: 'ElectricConsumptionUnit',
							unit2: 'FuelConsumptionUnit',
							digits: 1,
						},
						{
							key: 'wltp_co2_weighted_combined',
							headline: 'WltpConsumptionWeighted',
							unit: 'CO2ConsumptionUnit',
							digits: 1,
						},
						{
							key: 'electrical_range',
							headline: 'ElectricalRange',
							subtitle: true,
						},
						{
							key: 'wltp_range_combined',
							headline: 'WltpElectricalRangeCombined',
							unit: 'LengthUnit',
							round: true,
						},
						{
							key: 'wltp_range_city',
							headline: 'WltpElectricalRangeUrban',
							unit: 'LengthUnit',
							round: true,
						},
						{
							key: 'battery_data',
							headline: 'BatteryData',
							subtitle: true,
						},
						{
							key: 'battery_size',
							headline: 'OverviewBoxBattery',
							unit: 'BatteryUnit',
							engine: true,
						},
						{
							key: 'battery_charging_dc_100',
							headline: 'BatteryChargingDc100',
							engine: true,
							string: true,
						},
						{
							key: 'battery_charging_dc_max_power',
							headline: 'OverviewBoxChargingMaxPower',
							unit: 'FuelPowerUnit',
							round: true,
							engine: true,
						},
						{
							key: 'battery_charging_ac_100',
							headline: 'BatteryChargingAc100',
							engine: true,
							string: true,
						},
						{
							key: 'battery_charging_bidirectional',
							headline: 'OverviewBoxChargingBidirectional',
							engine: true,
							string: true,
						},
						{
							key: 'battery_charging_range_dc',
							headline: 'OverviewBoxChargingDCRange',
							unit: 'LengthUnit',
							round: true,
							engine: true,
							string: true,
						},
						{
							key: 'battery_nominal_voltage',
							headline: 'OverviewBoxBatteryNominalVoltage',
							unit: 'BatteryNominalVoltageUnit',
							round: true,
							engine: true,
							string: true,
						},
						{
							key: 'battery_type',
							headline: 'OverviewBoxBatteryType',
							engine: true,
							string: true,
						},
					],
					Diesel: [...generateConsumptionList('FuelConsumptionUnit')],
					Petrol: [...generateConsumptionList('FuelConsumptionUnit')],
					Gas: [...generateConsumptionList('GasConsumptionUnit')],
				},
			};

export function disclaimerTagsInConsumptionLists(
	engineType: keyof ConsumptionListType
) {
	const disclaimerTags: Array<string> = [];
	if (consumptionList.wltp) {
		const wltpConsumptions = consumptionList.wltp[engineType];

		if (wltpConsumptions) {
			[...wltpConsumptions].forEach(consumption => {
				if (
					consumption.disclaimer &&
					!disclaimerTags.includes(consumption.disclaimer)
				) {
					disclaimerTags.push(consumption.disclaimer);
				}
			});
		}
		return disclaimerTags;
	} else {
		return [];
	}
}

export function getConsumptionValue(
	c: ConsumptionElementType | undefined,
	vehicleDataModel: SSPContentFieldAccessor
) {
	// console.log(c);
	if (!c) return '-';
	const value =
		c.overwrite !== undefined && c.overwrite !== null
			? c.overwrite
			: vehicleDataModel.float(c.key, NaN);
	return c.efficiency && vehicleDataModel.str(c.key) !== 'none'
		? vehicleDataModel.str(c.key, '-')
		: c.key2
			? getNumberString(
					c.round ? Math.round(value) : value,
					i18next.language,
					i18next.t(c.unit ?? ''),
					c.digits
				) +
				' plus ' +
				getNumberString(
					c.round
						? Math.round(vehicleDataModel.float(c.key2, NaN))
						: vehicleDataModel.float(c.key2, NaN),
					i18next.language,
					i18next.t(c.unit2 ?? ''),
					c.digits
				)
			: getNumberString(
					c.round ? Math.round(value) : value,
					i18next.language,
					i18next.t(c.unit ?? ''),
					c.digits
				);
}

export function getBatteryValue(
	c: ConsumptionElementType | undefined,
	engine: SSPContentFieldAccessor
) {
	if (!c) return '-';
	const value =
		c.overwrite !== undefined && c.overwrite !== null
			? c.overwrite
			: c.string? engine.str(c.key) : engine.float(c.key, NaN);
	return c.efficiency && engine.str(c.key) !== 'none'
		? engine.str(c.key, '-')
		: typeof value === 'number' ? getNumberString(
				c.round ? Math.round(value) : value,
				i18next.language,
				i18next.t(c.unit ?? ''),
				c.digits
			) : value;
}
